import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { LocationAutocompleteInput } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';

import css from './LocationFilter.module.css';

const identity = v => v;

/** No icon - hide search icon in location filter */
const NoIcon = () => null;

/**
 * Location filter for search sidebar. Uses LocationAutocompleteInput and
 * submits address, bounds, and optionally origin only when a place is selected
 * from suggestions (no live update while typing).
 */
const LocationFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    label,
    getAriaLabel,
    onSubmit,
    urlQueryParams,
    isOriginInUse,
    ...rest
  } = props;

  const { address, bounds, origin } = urlQueryParams || {};
  const locationFieldsPresent = isOriginInUse
    ? address !== undefined && origin && bounds
    : address !== undefined && bounds;
  const isCurrentLocation = locationFieldsPresent && address === '';
  const plainInitialValues = locationFieldsPresent
    ? {
        location: {
          search: isCurrentLocation
            ? intl.formatMessage({ id: 'LocationAutocompleteInput.currentLocation' })
            : address,
          selectedPlace: { address: address ?? '', origin, bounds },
        },
      }
    : { location: null };

  const handleSubmit = values => {
    if (values === null) {
      onSubmit({ address: undefined, bounds: undefined, origin: undefined });
      return;
    }
    const place = values?.location?.selectedPlace;
    if (place) {
      const params = {
        address: place.address,
        bounds: place.bounds,
        ...(isOriginInUse && place.origin != null ? { origin: place.origin } : {}),
      };
      onSubmit(params);
    }
    /* While typing (no selection), do not call onSubmit - avoid refetch until user selects a suggestion */
  };

  const hasValue = !!locationFieldsPresent;
  const displayValue = hasValue
    ? isCurrentLocation
      ? intl.formatMessage({ id: 'LocationAutocompleteInput.currentLocation' })
      : address
    : null;
  const ariaLabel = getAriaLabel(label, displayValue);

  return (
    <FilterPlain
      rootClassName={classNames(rootClassName, css.root)}
      className={className}
      id={`${id}.plain`}
      label={label}
      ariaLabel={ariaLabel}
      isSelected={hasValue}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={plainInitialValues}
      {...rest}
    >
      <Field name="location" format={identity}>
        {({ input }) => {
          const { onChange, ...restInput } = input;
          return (
            <LocationAutocompleteInput
              id={`${id}-location-input`}
              className={css.inputRoot}
              CustomIcon={NoIcon}
              iconClassName={css.chipIcon}
              inputClassName={css.input}
              placeholder={intl.formatMessage({ id: 'LocationFilter.placeholder' })}
              closeOnBlur
              input={{ ...restInput, onChange }}
              submitButton={null}
              ariaLabel={ariaLabel}
            />
          );
        }}
      </Field>
    </FilterPlain>
  );
};

export default LocationFilter;
